import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Zap, Gift, Brain, X, ArrowRight } from "lucide-react";
import { useApp } from "../AppContext";

const STORAGE_KEY = "progere_onboarded_v1";

export default function OnboardingModal() {
  const { lang, user } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      // small delay so it doesn't fight with initial dashboard load
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [user]);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const STEPS = lang === "fr" ? [
    {
      icon: Sparkles,
      color: "#D4AF37",
      title: "Bienvenue dans PROGÈRE Devis AI",
      sub: "L'estimation intelligente pour les entrepreneurs du Québec.",
      body: "Créez des soumissions professionnelles en quelques secondes grâce à l'IA, exportez en PDF, et gardez tous vos chantiers sous contrôle.",
    },
    {
      icon: Brain,
      color: "#7BAEFA",
      title: "Génération IA Claude Sonnet 4.5",
      sub: "Décrivez, photographiez, ou téléversez un plan.",
      body: "Notre IA analyse votre demande (texte, photos de chantier, plans PDF) et génère une soumission détaillée avec tarifs CCQ et taxes du Québec.",
    },
    {
      icon: Zap,
      color: "#D4AF37",
      title: "3 soumissions d'essai gratuites",
      sub: "Aucune carte requise. Testez sans engagement.",
      body: "Vous avez 3 soumissions gratuites pour découvrir l'application. Quand vous êtes prêt, passez en Pro avec 14 jours d'essai gratuit supplémentaires.",
    },
    {
      icon: Gift,
      color: "#C00000",
      title: "Essai Pro 14 jours en bonus",
      sub: "Toutes les fonctionnalités Pro débloquées.",
      body: "Soumissions illimitées, templates personnalisés, IA avancée. Activable en un clic depuis la page Abonnement, sans carte de crédit.",
      cta: "Découvrir les plans",
      ctaAction: () => { close(); nav("/app/billing"); },
    },
  ] : [
    {
      icon: Sparkles,
      color: "#D4AF37",
      title: "Welcome to PROGÈRE Devis AI",
      sub: "Smart estimating for Quebec contractors.",
      body: "Create professional quotes in seconds with AI, export to PDF, and keep all your projects under control.",
    },
    {
      icon: Brain,
      color: "#7BAEFA",
      title: "Claude Sonnet 4.5 AI generation",
      sub: "Describe, photograph, or upload a plan.",
      body: "Our AI analyzes your input (text, site photos, PDF plans) and generates a detailed quote with CCQ rates and Quebec taxes.",
    },
    {
      icon: Zap,
      color: "#D4AF37",
      title: "3 free trial quotes",
      sub: "No card required. Test without commitment.",
      body: "You get 3 free quotes to explore the app. When you're ready, upgrade to Pro with 14 additional free trial days.",
    },
    {
      icon: Gift,
      color: "#C00000",
      title: "Bonus: 14-day Pro trial",
      sub: "All Pro features unlocked.",
      body: "Unlimited quotes, custom templates, advanced AI. One-click activation from the Subscription page, no credit card.",
      cta: "Explore plans",
      ctaAction: () => { close(); nav("/app/billing"); },
    },
  ];

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
        onClick={close}
        data-testid="onboarding-modal">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong w-full max-w-lg p-8 relative">
          {/* Close X — larger and more visible top-right */}
          <button
            onClick={close}
            aria-label={lang === "fr" ? "Fermer" : "Close"}
            title={lang === "fr" ? "Fermer" : "Close"}
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition active:scale-95 hover:brightness-110"
            style={{
              background: "linear-gradient(180deg, rgba(245,245,245,0.14), rgba(245,245,245,0.06))",
              border: "1px solid rgba(245,245,245,0.25)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
            }}
            data-testid="onboarding-close">
            <X size={16} color="#F5F5F5" strokeWidth={2.5} />
          </button>

          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
            style={{ background: `${s.color}22`, border: `1px solid ${s.color}44` }}>
            <Icon size={28} color={s.color} />
          </div>

          <h2 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }} data-testid="onboarding-title">
            {s.title}
          </h2>
          <p className="text-sm mb-4" style={{ color: s.color }}>{s.sub}</p>
          <p className="text-sm leading-relaxed mb-8" style={{ color: "rgba(245,245,245,0.7)" }}>{s.body}</p>

          <div className="flex items-center justify-between">
            <div className="flex gap-1.5">
              {STEPS.map((_, i) => (
                <div key={i} className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === step ? 24 : 8,
                    background: i === step ? "#D4AF37" : "rgba(245,245,245,0.15)",
                  }} />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="btn-ghost text-sm" data-testid="onboarding-prev">
                  {lang === "fr" ? "Précédent" : "Back"}
                </button>
              )}
              {isLast ? (
                <button onClick={s.ctaAction || close} className="btn-red text-sm" data-testid="onboarding-finish">
                  {s.cta || (lang === "fr" ? "Commencer" : "Get started")} <ArrowRight size={14} />
                </button>
              ) : (
                <button onClick={() => setStep(step + 1)} className="btn-red text-sm" data-testid="onboarding-next">
                  {lang === "fr" ? "Suivant" : "Next"} <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Skip link — always visible bottom of modal */}
          <div className="mt-4 pt-3 text-center" style={{ borderTop: "1px solid rgba(245,245,245,0.08)" }}>
            <button onClick={close} data-testid="onboarding-skip"
              className="text-xs underline underline-offset-2 hover:brightness-125 transition"
              style={{ color: "rgba(245,245,245,0.55)" }}>
              {lang === "fr" ? "Passer l'introduction et découvrir l'app" : "Skip intro and explore the app"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
